// Screen coordinates are supplied by the map projection at the current zoom.
// This helper samples an interaction; it never invents geographic area/precision.
const validSample = (point) => point && [point.lat, point.lng, point.x, point.y]
  .every((value) => typeof value === "number" && Number.isFinite(value))
  && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180;
const distanceSquared = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
const vertices = (points) => points.map(({ lat, lng }) => ({ lat, lng }));

function signedArea(points) {
  const origin = points[0];
  return points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + (point.x - origin.x) * (next.y - origin.y) - (next.x - origin.x) * (point.y - origin.y);
  }, 0) / 2;
}

function segmentDistanceSquared(point, a, b) {
  const length = distanceSquared(a, b);
  if (!length) return distanceSquared(point, a);
  const ratio = Math.max(0, Math.min(1, ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / length));
  return distanceSquared(point, { x: a.x + ratio * (b.x - a.x), y: a.y + ratio * (b.y - a.y) });
}

function simplifyOpen(points, tolerance) {
  if (points.length < 3 || tolerance === 0) return points;
  const keep = new Set([0, points.length - 1]);
  const segments = [[0, points.length - 1]];
  while (segments.length) {
    const [start, end] = segments.pop();
    let furthest = -1;
    let furthestDistance = tolerance ** 2;
    for (let index = start + 1; index < end; index += 1) {
      const distance = segmentDistanceSquared(points[index], points[start], points[end]);
      if (distance > furthestDistance) { furthest = index; furthestDistance = distance; }
    }
    if (furthest >= 0) {
      keep.add(furthest);
      segments.push([start, furthest], [furthest, end]);
    }
  }
  return points.filter((_, index) => keep.has(index));
}

function simplifyClosed(points, tolerance) {
  if (!tolerance) return points;
  // A closed first/last segment has zero length. Split at a distant anchor so
  // both arcs retain meaningful endpoints; omit the repeated closing vertex.
  let opposite = 1;
  for (let index = 2; index < points.length; index += 1) {
    if (distanceSquared(points[0], points[index]) > distanceSquared(points[0], points[opposite])) opposite = index;
  }
  return [
    ...simplifyOpen(points.slice(0, opposite + 1), tolerance).slice(0, -1),
    ...simplifyOpen([...points.slice(opposite), points[0]], tolerance).slice(0, -1),
  ];
}

function orientation(a, b, c) {
  const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  return Math.abs(cross) < 1e-9 ? 0 : Math.sign(cross);
}

function onSegment(point, a, b) {
  return point.x >= Math.min(a.x, b.x) - 1e-9 && point.x <= Math.max(a.x, b.x) + 1e-9
    && point.y >= Math.min(a.y, b.y) - 1e-9 && point.y <= Math.max(a.y, b.y) + 1e-9;
}

function intersects(a, b, c, d) {
  const abC = orientation(a, b, c), abD = orientation(a, b, d);
  const cdA = orientation(c, d, a), cdB = orientation(c, d, b);
  if (abC * abD < 0 && cdA * cdB < 0) return true;
  return (!abC && onSegment(c, a, b)) || (!abD && onSegment(d, a, b))
    || (!cdA && onSegment(a, c, d)) || (!cdB && onSegment(b, c, d));
}

function selfIntersects(points) {
  for (let first = 0; first < points.length; first += 1) {
    const firstEnd = (first + 1) % points.length;
    for (let second = first + 1; second < points.length; second += 1) {
      const secondEnd = (second + 1) % points.length;
      if (firstEnd === second || secondEnd === first) continue;
      if (intersects(points[first], points[firstEnd], points[second], points[secondEnd])) return true;
    }
  }
  return false;
}

function positive(value, label, integer = false) {
  if (!Number.isFinite(value) || value <= 0 || (integer && !Number.isInteger(value))) throw new RangeError(`Invalid ${label}`);
  return value;
}

export function createFreehandStroke({ sampleSpacingPx = 3, maxSamples = 2048 } = {}) {
  positive(sampleSpacingPx, "sample spacing");
  positive(maxSamples, "sample budget", true);
  if (maxSamples < 3 || maxSamples > 8192) throw new RangeError("Sample budget must be between 3 and 8192");
  const samples = [];
  let failure = null;
  return {
    add(point) {
      if (failure) return false;
      if (!validSample(point)) { failure = "invalid_sample"; return false; }
      if (samples.length && distanceSquared(samples[samples.length - 1], point) < sampleSpacingPx ** 2) return false;
      if (samples.length >= maxSamples) { failure = "too_many_samples"; return false; }
      samples.push({ lat: point.lat, lng: point.lng, x: point.x, y: point.y });
      return true;
    },
    preview() { return vertices(samples); },
    finish({ tolerancePx = 2, maxTolerancePx = 8, maxVertices = 256, minAreaPx2 = 16, previousPoints = [] } = {}) {
      positive(tolerancePx, "simplification tolerance");
      positive(maxTolerancePx, "maximum simplification tolerance");
      positive(maxVertices, "vertex budget", true);
      positive(minAreaPx2, "minimum screen area");
      if (maxVertices < 3 || maxVertices > 8192 || maxTolerancePx < tolerancePx) throw new RangeError("Invalid simplification limits");
      const fail = (reason) => ({ ok: false, reason, rawCount: samples.length });
      if (failure) return fail(failure);
      const closed = [...samples];
      while (closed.length > 1 && distanceSquared(closed[0], closed[closed.length - 1]) <= sampleSpacingPx ** 2) closed.pop();
      const distinct = new Set(closed.map(({ lat, lng }) => `${lat}:${lng}`));
      if (distinct.size < 3) return fail("too_few_points");
      if (distinct.size !== closed.length) return fail("self_intersection");
      if (selfIntersects(closed)) return fail("self_intersection");
      const area = signedArea(closed);
      if (Math.abs(area) < minAreaPx2) return fail("tiny_stroke");
      // Try the requested tolerance, then safer finer candidates, then bounded
      // coarser candidates only if needed to fit the vertex budget. Never truncate.
      const tolerances = [tolerancePx, tolerancePx / 2, 0];
      for (let tolerance = tolerancePx * 1.5; tolerance < maxTolerancePx; tolerance *= 1.5) tolerances.push(tolerance);
      tolerances.push(maxTolerancePx);
      for (const tolerance of [...new Set(tolerances)]) {
        const candidate = simplifyClosed(closed, tolerance);
        if (candidate.length < 3 || candidate.length > maxVertices) continue;
        const candidateArea = signedArea(candidate);
        if (Math.abs(candidateArea) < minAreaPx2 || Math.sign(candidateArea) !== Math.sign(area) || selfIntersects(candidate)) continue;
        const points = vertices(candidate);
        return {
          ok: true, closed: true, points, rawCount: samples.length, tolerancePx: tolerance,
          // Separate copies make this a stable before/after snapshot for Undo.
          undo: { before: vertices(previousPoints), after: vertices(candidate) },
        };
      }
      return fail("too_many_vertices");
    },
  };
}
